import { useContext } from 'react';

import { z } from 'zod';

import { Button } from '@mui/material';

import { ConfirmDialog } from '../../../components/dialogs';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { ArticleParentStrings } from '../../../constants';
import { useRemoveAdditionalParentCategory } from '../../../lib/post/hooks';

type RemoveParentCategoryButtonProps = {
  parentCategoryId: string;
  postId: string;
  subcategory?: boolean;
};

function RemoveParentCategoryButton({ parentCategoryId, postId, subcategory = false }: RemoveParentCategoryButtonProps) {
  const [, setAlert] = useContext(AlertSnackbarContext);
  const { mutateAsync: removeAdditionalParentCategory, isLoading: isSubmitting } = useRemoveAdditionalParentCategory();

  const handleSubmit = async (close: () => void) => {
    try {
      await removeAdditionalParentCategory({ postId, parentCategoryId });
      setAlert({
        message: subcategory ? ArticleParentStrings.SUBCATEGORY_REMOVED : ArticleParentStrings.CATEGORY_REMOVED,
        severity: 'success',
      });
      close();
    } catch (error) {
      const parsedError = z.object({ message: z.string() }).safeParse(error);
      setAlert({ message: parsedError.success ? parsedError.data.message : ArticleParentStrings.REMOVE_FAILED, severity: 'error' });
    }
  };

  return (
    <ConfirmDialog
      trigger={(open) => (
        <Button onClick={open} variant="outlined" color="error" size="small" type="button">
          {ArticleParentStrings.REMOVE}
        </Button>
      )}
      title={subcategory ? ArticleParentStrings.REMOVE_SUBCATEGORY : ArticleParentStrings.REMOVE_CATEGORY}
      description={subcategory ? ArticleParentStrings.CONFIRM_REMOVE_SUBCATEGORY : ArticleParentStrings.CONFIRM_REMOVE_CATEGORY}
      cancelBtnText={ArticleParentStrings.CANCEL}
      submitBtnText={ArticleParentStrings.SUBMIT}
      onConfirm={handleSubmit}
      disabled={isSubmitting}
      loading={isSubmitting}
    />
  );
}

export default RemoveParentCategoryButton;
